import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import AdminHeader from "@/components/admin/AdminHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getTeams } from "../api/teams";
import { useAuth } from "../contexts/AuthContext";
import { createAd, getAd, updateAd } from "../api/ads";

const empty = {
    customer_name: "",
    title: "",
    image_url: "",
    click_url: "",
    start_date: "",
    end_date: "",
    package: "bronze",
    placements: ["banner"],
    target_mode: "all",
    club_slugs: [],
};

const placementOptions = [
    { value: "banner", label: "Generell banner", description: "Fallback och generell placement för banners som inte är knutna till en specifik yta." },
    { value: "banner_home", label: "Hem - banner", description: "Aktiv generell annonsyta på startsidan under hero-sektionen." },
    { value: "banner_protocol", label: "Protokoll - banner", description: "Aktiv placement i matchprotokoll. Används i två slotar: topp och botten." },
    { value: "banner_live", label: "Live - banner", description: "Förberedd placement för framtida live-del. Ingen aktiv frontendyta ännu." },
];

const packageDescriptions = {
    bronze: "Basvikt 1. Lägst rotationsprioritet.",
    silver: "Vikt 2. Syns oftare än bronze.",
    gold: "Vikt 4. Tydligt högre prioritet.",
    platinum: "Vikt 8. Högst prioritet i rotationen.",
};

function slugifyTeamName(name) {
    return (name || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[åä]/g, "a")
        .replace(/[ö]/g, "o")
        .replace(/[^\w]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function normalizeUrl(value) {
    const trimmed = (value || "").trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

export default function AdFormPage() {
    const { adId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [form, setForm] = useState(empty);
    const [teams, setTeams] = useState([]);
    const isSuper = user?.role === "super_admin";

    useEffect(() => {
        if (!adId) return;
        getAd(adId).then((data) =>
            setForm({
                ...empty,
                ...data,
                placements: Array.isArray(data?.placements) && data.placements.length ? data.placements : [data?.placement || "banner"],
            })
        );
    }, [adId]);

    useEffect(() => {
        if (adId) return;
        const presetCustomer = (searchParams.get("customer") || "").trim();
        if (!presetCustomer) return;
        setForm((current) => ({
            ...current,
            customer_name: current.customer_name || presetCustomer,
        }));
    }, [adId, searchParams]);

    useEffect(() => {
        getTeams().then((data) => setTeams(Array.isArray(data) ? data : [])).catch(() => setTeams([]));
    }, []);

    const teamOptions = useMemo(
        () =>
            teams
                .map((team) => ({
                    ...team,
                    adSlug: (team.slug || "").trim().toLowerCase() || slugifyTeamName(team.name) || team.id,
                }))
                .filter((team) => team.adSlug),
        [teams]
    );

    const submit = async (e) => {
        e.preventDefault();
        if (form.target_mode === "clubs" && !(form.club_slugs || []).length) {
            toast.error("Välj minst ett lag för annonsen.");
            return;
        }
        try {
            if (adId) {
                const payload = {
                    customer_name: form.customer_name,
                    title: form.title,
                    image_url: normalizeUrl(form.image_url),
                    click_url: normalizeUrl(form.click_url),
                    start_date: form.start_date,
                    end_date: form.end_date,
                    placements: form.placements,
                    target_mode: form.target_mode,
                    club_slugs: form.club_slugs,
                    ...(isSuper ? { package: form.package } : {}),
                };
                await updateAd(adId, payload);
                toast.success("Annons uppdaterad");
            } else {
                const payload = {
                    ...form,
                    placements: form.placements,
                    image_url: normalizeUrl(form.image_url),
                    click_url: normalizeUrl(form.click_url),
                };
                await createAd(payload);
                toast.success("Annons skapad");
            }
            navigate("/admin/ads", { replace: true });
        } catch (err) {
            toast.error(err?.message || "Kunde inte spara annonsen.");
        }
    };

    const selectedPlacements = useMemo(
        () => placementOptions.filter((option) => (form.placements || []).includes(option.value)),
        [form.placements]
    );
    const placementLabel = useMemo(
        () => (selectedPlacements.length ? selectedPlacements.map((option) => option.label).join(", ") : "Generell banner"),
        [selectedPlacements]
    );
    const placementDescription = useMemo(
        () => selectedPlacements.map((option) => option.description).join(" "),
        [selectedPlacements]
    );
    const selectedTeamNames = useMemo(() => {
        const selected = new Set(form.club_slugs || []);
        return teamOptions.filter((team) => selected.has(team.adSlug)).map((team) => team.name);
    }, [form.club_slugs, teamOptions]);

    function toggleClubSlug(slug, checked) {
        setForm((current) => {
            const next = new Set(current.club_slugs || []);
            if (checked) next.add(slug);
            else next.delete(slug);
            return { ...current, club_slugs: Array.from(next) };
        });
    }

    function togglePlacement(value, checked) {
        setForm((current) => {
            const next = new Set(current.placements || []);
            if (checked) next.add(value);
            else next.delete(value);
            return { ...current, placements: next.size ? Array.from(next) : ["banner"] };
        });
    }

    return (
        <div className="min-h-screen bg-background">
            <AdminHeader />
            <main className="container mx-auto px-4 py-6 space-y-6">
                <Card className="max-w-4xl border-border/80 shadow-sm">
                    <CardHeader className="space-y-2">
                        <CardTitle>{adId ? "Redigera annons" : "Ny annons"}</CardTitle>
                        <CardDescription>
                            Skapa en klickbar banner med styrning för lag, annonsytor, period och paket.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={submit} className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="customer_name">Företag / kundnamn</Label>
                                    <Input
                                        id="customer_name"
                                        value={form.customer_name}
                                        onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                                        placeholder="Exempel AB"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="title">Titel</Label>
                                    <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="image_url">Bild-URL</Label>
                                    <Input id="image_url" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." required />
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="click_url">Mållänk</Label>
                                    <Input id="click_url" value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })} placeholder="https://..." required />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="start_date">Startdatum</Label>
                                        <Input id="start_date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="end_date">Slutdatum</Label>
                                        <Input id="end_date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Annonsytor</Label>
                                    <div className="space-y-3 rounded-lg border p-4">
                                        {placementOptions.map((option) => {
                                            const checked = (form.placements || []).includes(option.value);
                                            return (
                                                <label key={option.value} className="flex items-start gap-3 rounded-md border p-3 text-sm">
                                                    <Checkbox checked={checked} onCheckedChange={(next) => togglePlacement(option.value, Boolean(next))} />
                                                    <span className="space-y-1">
                                                        <span className="block font-medium text-foreground">{option.label}</span>
                                                        <span className="block text-xs text-muted-foreground">{option.description}</span>
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Målgrupp</Label>
                                    <Select
                                        value={form.target_mode}
                                        onValueChange={(value) =>
                                            setForm((current) => ({
                                                ...current,
                                                target_mode: value,
                                                club_slugs: value === "all" ? [] : current.club_slugs,
                                            }))
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Välj målgrupp" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-popover text-popover-foreground border-border">
                                            <SelectItem value="all">Nationell annons</SelectItem>
                                            <SelectItem value="clubs">Valda lag</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {form.target_mode === "clubs" ? (
                                    <div className="space-y-3 rounded-lg border p-4">
                                        <div className="text-sm font-medium text-foreground">Välj ett eller flera lag</div>
                                        <div className="grid gap-3 sm:grid-cols-2">
                                            {teamOptions.map((team) => {
                                                const checked = form.club_slugs.includes(team.adSlug);
                                                return (
                                                    <label key={team.id || team.adSlug} className="flex items-center gap-3 rounded-md border p-3 text-sm">
                                                        <Checkbox checked={checked} onCheckedChange={(next) => toggleClubSlug(team.adSlug, Boolean(next))} />
                                                        <span>{team.name}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : null}

                                {isSuper ? (
                                    <div className="space-y-2">
                                        <Label>Paket</Label>
                                        <Select value={form.package} onValueChange={(value) => setForm({ ...form, package: value })}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Välj paket" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-popover text-popover-foreground border-border">
                                                <SelectItem value="bronze">Bronze</SelectItem>
                                                <SelectItem value="silver">Silver</SelectItem>
                                                <SelectItem value="gold">Gold</SelectItem>
                                                <SelectItem value="platinum">Platinum</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                ) : null}

                                <Button type="submit" className="w-full md:w-auto">
                                    {adId ? "Spara ändringar" : "Skapa annons"}
                                </Button>
                            </div>

                            <div className="space-y-4">
                                <Card className="border-dashed">
                                    <CardHeader>
                                        <CardTitle className="text-base">Översikt</CardTitle>
                                        <CardDescription>Så här kommer annonsen att styras.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-3 text-sm text-muted-foreground">
                                        <div>
                                            <div className="font-medium text-foreground">Kund</div>
                                            <div>{form.customer_name || "-"}</div>
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground">Annonsytor</div>
                                            <div>{placementLabel}</div>
                                            {placementDescription ? (
                                                <div className="mt-1 text-xs text-muted-foreground">{placementDescription}</div>
                                            ) : null}
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground">Målgrupp</div>
                                            <div>
                                                {form.target_mode === "all"
                                                    ? "Nationell annons"
                                                    : selectedTeamNames.length
                                                    ? selectedTeamNames.join(", ")
                                                    : "Inga lag valda"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground">Klubb</div>
                                            <div>{form.target_mode === "all" ? "all" : (form.club_slugs || []).join(", ")}</div>
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground">Paket</div>
                                            <div>{packageDescriptions[form.package] || packageDescriptions.bronze}</div>
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground">Klickspårning</div>
                                            <div>Klick loggas server-side innan användaren skickas vidare till mållänken.</div>
                                        </div>
                                    </CardContent>
                                </Card>

                                <div className="space-y-2">
                                    <Label htmlFor="ad-notes">Intern anteckning</Label>
                                    <Textarea
                                        id="ad-notes"
                                        value={`Kund: ${form.customer_name || "-"}\nAnnonsytor: ${placementLabel}\nMålgrupp: ${form.target_mode === "all" ? "Nationell" : selectedTeamNames.join(", ") || "Inga lag valda"}\nPaket: ${form.package}`}
                                        readOnly
                                        className="min-h-[132px]"
                                    />
                                </div>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </main>
        </div>
    );
}
